import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Itinerary,
  LocationDataGeocoded,
  LocationData,
  CountryBounds,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocationHttpService {
  private readonly AF_ADDRESS_TO_GEOCODE_URL =
    '/api/location/address/togeocode';
  private readonly AF_GET_COUNTRY_BOUNDS_URL =
    '/api/location/address/countrybounds';
  private readonly AF_GOOGLE_PLACES_URL = '/api/location/fromaddress';
  private readonly AF_PLACE_ID_TO_GEOCODE_URL =
    '/api/location/place/togeocode?placeId=';
  private readonly AF_GEOCODE_TO_ADDRESS_URL = '/api/location/fromgeocode';
  private readonly AF_TRAJET_GOOGLE_URL = '/api/location/itinerary';
  private readonly AF_GET_CITIES_BY_POSTAL_CODE = '/api/location/bypostalcode';
  private readonly AF_POSTAL_CODE_FROM_GEOCODE =
    '/api/location/postalcode-by-geocode';

  constructor(private readonly httpClient: HttpClient) {}

  getGeoCodeFromAddress(address: string): Observable<LocationDataGeocoded> {
    return this.httpClient.post<LocationDataGeocoded>(
      this.AF_ADDRESS_TO_GEOCODE_URL,
      { address },
      { headers: { requestName: 'location-http getGeoCodeFromAddress' } }
    );
  }

  getCountryBounds(): Observable<CountryBounds> {
    return this.httpClient.get<CountryBounds>(this.AF_GET_COUNTRY_BOUNDS_URL, {
      headers: { requestName: 'location-http getCountryBounds' },
    });
  }

  getPlacesFromAddress(address: string): Observable<LocationData[]> {
    return this.httpClient.post<LocationData[]>(
      this.AF_GOOGLE_PLACES_URL,
      { address: address },
      { headers: { requestName: 'location-http getPlacesFromAddress' } }
    );
  }

  getGeoCodeFromPlaceId(placeId: string): Observable<LocationDataGeocoded> {
    return this.httpClient.get<LocationDataGeocoded>(
      this.AF_PLACE_ID_TO_GEOCODE_URL + placeId,
      { headers: { requestName: 'location-http getGeoCodeFromPlaceId' } }
    );
  }

  getPlaceDataFromGeoCode(
    lat: number,
    lon: number
  ): Observable<LocationDataGeocoded> {
    return this.httpClient.get<LocationDataGeocoded>(
      this.AF_GEOCODE_TO_ADDRESS_URL + '?lat=' + lat + '&lon=' + lon,
      { headers: { requestName: 'location-http getPlaceDataFromGeoCode' } }
    );
  }

  getGoogleItinerary(
    origin: string,
    destination: string,
    departureDateTimeInMillis: number
  ): Observable<Itinerary> {
    const body = {
      date: departureDateTimeInMillis,
      origin,
      destination,
    };

    return this.httpClient.post<Itinerary>(this.AF_TRAJET_GOOGLE_URL, body, {
      headers: { requestName: 'location-http getGoogleItinerary' },
    });
  }

  getPlacesFromPostalCode(postalCode: number) {
    return this.httpClient.post<LocationDataGeocoded[]>(
      this.AF_GET_CITIES_BY_POSTAL_CODE,
      { cp: postalCode },
      { headers: { requestName: 'location-http getPlacesFromPostalCode' } }
    );
  }

  getPostalCodeFromGeoCode(geoCoordinates) {
    return this.httpClient.post<LocationDataGeocoded>(
      this.AF_POSTAL_CODE_FROM_GEOCODE,
      { geoCoordinates },
      { headers: { requestName: 'location-http getPlacesFromPostalCode' } }
    );
  }
}
